import React from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import { Popover, Table, Whisper } from 'rsuite';
import { isEmpty } from 'lodash';
import { AdditionalPrice } from '../../api/apiTypes';

const EMPTY_VALUE = '–';

const COLUMNS: Column[] = [
  { key: 'marketplaceCode', label: 'Marketplace', width: 120 },
  { key: 'price', label: 'Price', width: 120 },
  { key: 'minimumPrice', label: 'Minimum price', width: 120 },
  { key: 'forcedPrice', label: 'Forced Price', width: 120 },
  { key: 'vat', label: 'VAT', width: 80 },
  { key: 'pricingRuleId', label: 'Price rule ID', width: 200 },
];

const TABLE_WIDTH = COLUMNS.reduce((width, column) => width + column.width, 0);

interface Props {
  additionalPrices?: AdditionalPrice[];
}

interface Row {
  marketplaceCode: string;
  price: string;
  minimumPrice: string;
  forcedPrice: string;
  vat: string;
  pricingRuleId: string;
  base: boolean;
}

interface Column {
  key: keyof Omit<Row, 'base'>;
  label: string;
  width: number;
}

function format<T>(value: T | null | undefined, formatter: (value: T) => string = String): string {
  if (value === null || value === undefined) {
    return EMPTY_VALUE;
  }

  return formatter(value);
}

function formatPrice(price: number | null | undefined, currency: string): string {
  return format(price, value => `${value} ${currency}`.trim());
}

function mapRow(additionalPrice: AdditionalPrice): Row {
  const currency = additionalPrice.currency ?? '';

  return {
    marketplaceCode: additionalPrice.marketplaceCode,
    price: formatPrice(additionalPrice.price, currency),
    minimumPrice: formatPrice(additionalPrice.minimumPrice, currency),
    forcedPrice: formatPrice(additionalPrice.forcedPrice, currency),
    vat: format(additionalPrice.vat, value => `${value}%`),
    pricingRuleId: format(additionalPrice.pricingRuleId || null),
    base: additionalPrice.base,
  };
}

export default function AdditionalPricePopover({ additionalPrices = [] }: Props) {
  if (isEmpty(additionalPrices)) {
    return null;
  }

  const data = additionalPrices.map(mapRow);

  const speaker = (
    <Popover arrow={false} style={{ maxWidth: 'calc(100vw - 40px)' }}>
      <div style={{ overflowX: 'auto' }}>
        <Table autoHeight bordered data={data} width={TABLE_WIDTH}>
          {COLUMNS.map(({ key, label, width }) => (
            <Table.Column key={key} width={width} align="center">
              <Table.HeaderCell>{label}</Table.HeaderCell>
              <Table.Cell>
                {(rowData: Row) => (
                  <span style={{ fontWeight: rowData.base ? 'bold' : undefined }}>
                    {rowData[key]}
                  </span>
                )}
              </Table.Cell>
            </Table.Column>
          ))}
        </Table>
      </div>
    </Popover>
  );

  return (
    <Whisper
      trigger="click"
      placement="autoVerticalStart"
      preventOverflow
      speaker={speaker}
    >
      <span>
        <FaInfoCircle cursor="pointer" style={{ display: 'block' }} />
      </span>
    </Whisper>
  );
}

AdditionalPricePopover.defaultProps = {
  additionalPrices: undefined,
};
